import React from "react";
import CodeBlock from "@/components/CodeBlock";

const TestDoc = () => {
  return (
    <section className="mb-20 text-slate-300">
      {/* TITLE */}
      <h2 className="text-3xl font-bold text-white mb-6">
        CLI Command — add test
      </h2>

      <p className="mb-6">
        The <strong>add test</strong> command generates a complete automated
        unit-testing setup for a selected domain feature. These tests run fully
        in-memory (no database required) and validate the behavior of all CQRS
        handlers in the feature module.
      </p>

      {/* BASIC USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Basic Usage
        </h3>

        <CodeBlock language="bash" code={`axisnet add test --name Employee`} />

        <p className="mt-4">
          This command generates two key components inside your Test layer:
        </p>

        <CodeBlock
          language="bash"
          code={`Tests/
  ├── Features/
  │   └── Employee/
  │       └── EmployeeFeatureTests.cs
  └── Fakes/
      └── FakeEmployeeRepository.cs`}
        />
      </div>

      {/* WHAT IS GENERATED */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          What Does This Generator Produce?
        </h3>

        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Feature Test Suite</strong>
            Includes Create, Update, Delete, GetById and GetList handler tests.
          </li>
          <li>
            <strong>Fake Repository</strong>
            An in-memory repository fully compatible with AxisNet’s
            <code>IAsyncRepository</code> interface.
          </li>
          <li>
            <strong>Fake Localization Service</strong> (if not already created)
          </li>
          <li>
            <strong>Reflection-based TestValueGenerator</strong>
            Automatically builds dummy request objects for testing.
          </li>
        </ul>
      </div>

      {/* FEATURE TEST FILE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mt-12 mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Feature Test File — Overview
        </h3>

        <p className="mb-4">
          The generated test suite verifies the full lifecycle of a feature:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>Create Handler</li>
          <li>Update Handler</li>
          <li>Delete Handler</li>
          <li>GetById Handler</li>
          <li>GetList Handler</li>
        </ul>

        <p>It ensures correct integration between:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Business Rules</li>
          <li>AutoMapper Profiles</li>
          <li>Fake Repository</li>
          <li>CQRS Handlers</li>
        </ul>
      </div>

      {/* HIGHLIGHT TEST STRUCTURE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          Test Suite Structure
        </h3>

        <CodeBlock
          language="bash"
          code={`public class {{DomainName}}FeatureTests
{
    // AutoMapper Profile Loader
    private readonly IMapper _mapper;

    // In-memory fake repository
    private readonly I{{DomainName}}Repository _repository;

    // Business rule validator
    private readonly {{DomainName}}BusinessRules _businessRules;

    // All handlers tested:
    // • Create{{DomainName}}Handler
    // • Update{{DomainName}}Handler
    // • Delete{{DomainName}}Handler
    // • Get{{DomainName}}ByIdHandler
    // • Get{{DomainName}}ListHandler
}`}
        />

        <p className="mt-4">
          All tests follow the Arrange → Act → Assert pattern.
        </p>
      </div>

      {/* FAKE REPOSITORY */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          In-Memory Fake Repository
        </h3>

        <p className="mb-4">
          The CLI generates an in-memory fake repository implementing both:
        </p>

        <ul className="list-disc pl-6 space-y-1 mb-4">
          <li>
            <code>I{`{{DomainName}}`}Repository</code>
          </li>
          <li>
            <code>IAsyncRepository&lt;{`{{DomainName}}`}, Guid&gt;</code>
          </li>
        </ul>

        <CodeBlock
          language="csharp"
          code={`private readonly List<{{DomainName}}> _store = new();

public Task<{{DomainName}}> AddAsync({{DomainName}} entity)
{
    entity.Id = Guid.NewGuid();
    _store.Add(entity);
    return Task.FromResult(entity);
}`}
        />

        <p className="mt-4">This makes your tests:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>Fast</li>
          <li>Completely isolated</li>
          <li>Deterministic</li>
          <li>No database or EF Core dependencies</li>
        </ul>
      </div>

      {/* BUSINESS RULES */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-4">
          Business Rule Testing
        </h3>

        <p className="mb-4">Each test initializes business rules with:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>FakeRepository</li>
          <li>FakeLocalizationService</li>
        </ul>

        <CodeBlock
          language="csharp"
          code={`_businessRules = new {{DomainName}}BusinessRules(_repository, new FakeLocalizationService());`}
        />

        <p className="mt-4">Meaning all rule validations like:</p>

        <ul className="list-disc pl-6 space-y-1">
          <li>MustBe{`{{DomainName}}`}Exists</li>
          <li>Id validation</li>
          <li>Uniqueness checks</li>
        </ul>

        <p>…are executed normally.</p>
      </div>

      {/* TEST VALUE GENERATOR */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg mb-12">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Automatic Test Object Generator
        </h3>

        <p className="mb-4">
          The CLI uses a reflection-based <code>TestValueGenerator</code> to
          automatically generate dummy command objects.
        </p>

        <CodeBlock
          language="csharp"
          code={`var command = TestValueGenerator.CreateInstance<Create{{DomainName}}Command>();`}
        />

        <p className="mt-4">
          This eliminates manual object construction in tests.
        </p>
      </div>

      {/* USAGE */}
      <div className="bg-slate-900/50 border border-slate-800 p-6 rounded-lg">
        <h3 className="text-2xl font-semibold text-emerald-400 mb-3">
          Running The Tests
        </h3>

        <p className="mb-4">
          Once generated, you can run your test suite using:
        </p>

        <CodeBlock language="bash" code={`dotnet test`} />

        <p className="mt-4">
          All tests execute extremely fast because the fake repository uses
          in-memory collections.
        </p>
      </div>
    </section>
  );
};

export default TestDoc;
